import Tooltip from '@material-ui/core/Tooltip';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useLocation, useParams } from 'react-router-dom';

import DateRangeComponent from '@components/DateRangeComponent';
import { toastError, toastSuccess } from '@components/Toast';

import OrderDashboard from '@features/order-dashboard/OrderDashboard';
import ExFactorySupplierQuote from '@features/order-dashboard/component/ExFactorySupplierQuote';
import StyleCard from '@features/order/components/StyleCard';
import CreateTna from '@features/order/components/TNA/create-tna';
import EtdDetails from '@features/order/components/TNA/EtdDetails';
import SearchTnaProduct from '@features/order/components/TNA/SearchTnaProduct';
import { PLANNING_STATUS, canEditTaskDueDate } from '@features/order/utils/projectUtil';
import EtdDateFilter from '@features/order-dashboard/component/EtdDateFilter';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import { fetchOrderDashboardHeadings } from '@store/actions/OrderDashboardAction';

import { getOrderByOrderDashboardId } from '@services/orderDashboard';
import tnaService from '@services/tna-service';

import ApiClient from '@lib/apiClient';

import { printApiError, STATIC_SET_CONSTANTS } from '@utils/Util';

const TnaRunning = ({ orderId }) => {
	const [productList, setProductList] = useState([]);
	const [isAllSelected, setisAllSelected] = useState(true);
	const [selectedStyleRfqId, setSelectedStyleRfqId] = useState(null);
	const [selecteStylePlanningDetail, setSelecteStylePlanningDetail] = useState([]);
	const [, setPlanningStatus] = useState(null);
	const [projectList, setProjectList] = useState([]);
	const [isReview, setIsReview] = useState(null);
	const [productsOfSet, setProductsOfSet] = useState([]);
	const [isTnaHasSetProduct, setIsTnaHasSetProduct] = useState(false);
	const [activeSetMemberId, setActiveSetMemberId] = useState(null);
	const [isFinishTabVisible, setIsFinishTabVisible] = useState(false);
	const [urlRfqId, setUrlRfqId] = useState(null);
	const dispatch = useDispatch();
	const location = useLocation();
	const { id } = useParams();
	const [searchKey, setSearchKey] = useState('');
	const [listOfAllProduct, setListOfAllProduct] = useState([]);
	const [selectedStyle, setSelectedStyle] = useState(null);
	const [rfqStatusInfoList, setRfqStatusInfoList] = useState([]);
	const [selectedETD, setSelectedETD] = useState(0);
	const [selectedIndex] = useState(0);
	const [selectedDate, setSelectedDate] = useState('All');

	const getUniqueFactoryDeliveryDates = (items) => {
		const factoryDeliveryDates = items.map((item) => item.factoryDeliveryDate);
		return [...new Set(factoryDeliveryDates)];
	};

	const uniqueFactoryDeliveryDates = getUniqueFactoryDeliveryDates(productList);

	const handleDateSelection = (date) => {
		let filteredProduct = productList;
		if (date !== 'All') {
			filteredProduct = productList.filter((item) => item.factoryDeliveryDate === date);
		}
		setListOfAllProduct(filteredProduct);
		setSelectedDate(date);
	};

	useEffect(() => {
		if (listOfAllProduct.length > 0) {
			setRfqStatusInfoList(listOfAllProduct[0].rfqStatusInfoList);
		}
	}, [listOfAllProduct]);

	useEffect(() => {
		if (rfqStatusInfoList?.length > 0) {
			const baseRfq = rfqStatusInfoList?.find((rfq) => rfq.isBaseRfq);
			if (baseRfq) {
				setSelectedETD(baseRfq.rfqId);
			}
		}
	}, [rfqStatusInfoList]);

	useEffect(() => {
		setListOfAllProduct(productList);
	}, [JSON.stringify(productList)]);

	const handleSearch = (key) => {
		if (key === '') {
			setListOfAllProduct(productList);
		} else {
			const result = productList.filter((item) =>
				item?.referenceNumber.toLowerCase().includes(key.toLowerCase())
			);
			setListOfAllProduct(result);
		}
		setSearchKey(key);
	};

	useEffect(() => {
		const rfqIdParam = new URLSearchParams(location.search).get('rfqId');
		const rfqId = rfqIdParam && rfqIdParam.includes('?') ? rfqIdParam.split('?')[0] : rfqIdParam;
		setUrlRfqId(rfqId);
	}, [location.search]);

	useEffect(() => {
		if (urlRfqId && productList.length > 0) {
			const style = productList.find((item) => item.rfqId === parseInt(urlRfqId));
			onSelectStyle(urlRfqId);
			setSelectedStyle(style);
			style?.planningStatus === PLANNING_STATUS.PENDING ||
			style?.planningStatus === PLANNING_STATUS.SUBMITTED
				? onSelectStyleForPending(style?.rfqId)
				: onSelectStyle(style?.rfqId);
		}
	}, [urlRfqId, productList.length]);

	const onChangeDateRange = async (value1, _value2, id) => {
		const body = {
			endDate: value1
		};
		const endDateUpdateResponse = await tnaService.taskEndDateUpdate(body, id);
		if (endDateUpdateResponse?.success) {
			if (activeSetMemberId && !isFinishTabVisible) {
				getStyleDetails(selectedStyleRfqId, true, activeSetMemberId);
			} else {
				getStyleDetails(selectedStyleRfqId);
			}
			toastSuccess(endDateUpdateResponse?.message);
		}
	};

	// The table lists the styles in the same order as the strip of style cards above it, so a
	// style sits in the same place in both. The strip follows the product list, and searching or
	// filtering it only takes cards away, never reorders them.
	const orderedProjectList = useMemo(() => {
		if (!projectList?.length) return projectList;

		const positionByStyle = new Map();
		productList.forEach((style, index) => {
			if (style?.id != null) positionByStyle.set(`product-${style.id}`, index);
			if (style?.rfqId != null) positionByStyle.set(`rfq-${style.rfqId}`, index);
		});

		// A style the strip does not know about keeps its own order, after the ones it does.
		const positionOf = (item) => {
			const position =
				positionByStyle.get(`product-${item?.productId}`) ?? positionByStyle.get(`rfq-${item?.id}`);
			return position === undefined ? Number.MAX_SAFE_INTEGER : position;
		};

		return projectList
			.map((item, index) => ({ item, index }))
			.sort((a, b) => positionOf(a.item) - positionOf(b.item) || a.index - b.index)
			.map(({ item }) => item);
	}, [projectList, productList]);

	const fetchDashboardInfo = async () => {
		const data = await tnaService.getOrderDashboardInfoByOrderId(id);
		await getProductList();
		setProjectList(data?.data || []);
	};

	const getAllStylePlanning = async () => {
		dispatch(openLoader());
		await fetchDashboardInfo();
		dispatch(closeLoader());
	};

	const getStyleDetails = async (rfqId, isLoading = true, idOfSetMember) => {
		if (isLoading) {
			dispatch(openLoader());
		}
		let stylePlanningDetailResponse;

		if (idOfSetMember) {
			stylePlanningDetailResponse = await tnaService.getRunningTnaStyleDetails(
				orderId,
				rfqId,
				idOfSetMember
			);
		} else {
			stylePlanningDetailResponse = await tnaService.getRunningTnaStyleDetails(orderId, rfqId);
		}
		setSelecteStylePlanningDetail(stylePlanningDetailResponse);
		if (isLoading) {
			dispatch(closeLoader());
		}
	};

	const getProductList = async () => {
		try {
			dispatch(openLoader());
			const { data } = await ApiClient.GET('getProjectProducts', orderId);
			setProductList(data);
			const updateSelectedStyle = data.find((item) => item?.id === selectedStyle?.id);
			if (updateSelectedStyle) {
				setSelectedStyle(updateSelectedStyle);
			}
		} catch (error) {
			toastError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const handleIndividualTna = (rfqId) => {
		getProductList();
		onSelectStyle(rfqId);
	};

	useEffect(() => {
		dispatch(fetchOrderDashboardHeadings(orderId));
		getAllStylePlanning(orderId);
	}, [orderId]);

	const onSelectStyle = (rfqId) => {
		setisAllSelected(false);
		setPlanningStatus('');
		setSelectedStyleRfqId(rfqId);
		const getSelectedStyle = productList.find(
			(product) =>
				parseInt(product.rfqId) === parseInt(rfqId) &&
				product?.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT
		);
		if (getSelectedStyle && getSelectedStyle.setMemberList) {
			extractAndOrganizeSetMembers([getSelectedStyle]);
			setActiveSetMemberId(getSelectedStyle?.setMemberList[0]?.id);
			setIsTnaHasSetProduct(true);
			getStyleDetails(rfqId, '', getSelectedStyle?.setMemberList[0]?.id);
		} else {
			setActiveSetMemberId(null);
			setIsTnaHasSetProduct(false);
			getStyleDetails(rfqId);
		}
	};

	const onChangeTabOfSetMember = (member, index) => {
		if (activeSetMemberId !== member.id) {
			if (productsOfSet.length - 1 === index) {
				getStyleDetails(member?.rfqId, true);
			} else {
				getStyleDetails(member?.rfqId, true, member?.id);
			}
			setActiveSetMemberId(member?.id);
		}
	};

	const extractAndOrganizeSetMembers = (designsList = []) => {
		const membersOfSet = [];
		designsList.forEach((design) => {
			if (design.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT && design.setMemberList) {
				design.setMemberList.forEach((member) => {
					membersOfSet.push({
						...member
					});
				});
				membersOfSet.push({
					name: 'Finish',
					id: design?.id,
					rfqId: design?.rfqId
				});
			}
		});
		setProductsOfSet(membersOfSet);
	};

	const onSelectStyleForPending = (rfqId) => {
		setisAllSelected(false);
		setSelectedStyleRfqId(rfqId);
		setPlanningStatus('PENDING');
	};

	const onApproveTask = async (taskId) => {
		dispatch(openLoader());
		const response = await tnaService.onApproveTask(taskId);
		if (response?.success) {
			if (activeSetMemberId && !isFinishTabVisible) {
				getStyleDetails(selectedStyleRfqId, true, activeSetMemberId);
			} else {
				getStyleDetails(selectedStyleRfqId);
			}
		}
		dispatch(closeLoader());
	};

	const onSelectFinishTab = () => {
		if (productsOfSet.length > 0) {
			const getFinshTab = productsOfSet.find((item) => item.id === activeSetMemberId);
			if (getFinshTab && getFinshTab.name === 'Finish') {
				setIsFinishTabVisible(true);
			} else {
				setIsFinishTabVisible(false);
			}
		}
	};

	const updateOrderStepByOrders = (orders) => {
		let modifiedProjectList = projectList?.map((item) => {
			let selectedOrder = orders.find((order) => order.id === item.id);
			if (selectedOrder) {
				item = selectedOrder;
			}
			return item;
		});
		setProjectList(modifiedProjectList);
	};

	useEffect(() => {
		onSelectFinishTab();
	}, [activeSetMemberId]);

	const renderSetMembersTab = () => {
		return (
			<div className="inner-tabs mt-3 mb-2">
				{isTnaHasSetProduct && (
					<ul>
						{productsOfSet.map((member, index) => (
							<li
								key={member?.id}
								className={`${activeSetMemberId === member.id ? 'active' : ''}`}
								onClick={() => onChangeTabOfSetMember(member, index)}
							>
								{index !== productsOfSet.length - 1 && 'TNA for'} {member?.name}
							</li>
						))}
					</ul>
				)}
			</div>
		);
	};

	const returnStatusIcon = (task, deliverableId, style) => {
		if (
			task?.formattedTaskStatus === 'EXPIRED' ||
			task?.formattedTaskStatus === 'PENDING' ||
			task?.formattedTaskStatus === 'RUNNING'
		)
			return (
				<>
					<Tooltip title="Complete" placement="top-start" arrow>
						<img
							src={`${process.env.PUBLIC_URL}/icons/due-green.svg`}
							alt="due"
							className="state"
							onClick={() => {
								if (task?.stepName === 'Handover to forwarder') {
									setIsReview(task.id);
								} else onApproveTask(task.id);
							}}
						/>
					</Tooltip>
					{isReview === task.id && (
						<ExFactorySupplierQuote
							rfqId={selectedStyleRfqId}
							onCloseModal={() => setIsReview(null)}
							onSuccess={() => {
								onApproveTask(task.id);
								setIsReview(null);
							}}
							supplierDetails={{
								supplierName: style?.deliverableResponse?.supplierName,
								supplierId: style?.deliverableResponse?.supplierId
							}}
						/>
					)}
				</>
			);
		else
			return (
				<Tooltip title="Completed" placement="top-start" arrow>
					<img src={`${process.env.PUBLIC_URL}/icons/Success.svg`} alt="" className="state" />
				</Tooltip>
			);
	};

	const renderInputTargetItem = (task) => {
		let title = Math.sign(task?.extraProductionTargetPercentage) === -1 ? 'PO' : 'PO+';
		if (task?.productionTarget) {
			return (
				<div className="production-details">
					<div className="production-details-btn">
						<span className="blue_dark_01">{task?.actualProductionQuantity || 0}</span>/
						{task?.productionTarget}
						<span className="percentage pl-1">
							({title}
							{parseFloat(task.extraProductionTargetPercentage).toFixed(2)}%)
						</span>
						<span className="devider">|</span> Bal:
						<span className="gray_dark_01">
							{parseInt(task?.productionTarget) - parseInt(task?.actualProductionQuantity || 0)}
						</span>
					</div>
				</div>
			);
		}
	};

	const returnTaskRow = (task, length, deliverableId, style, isMaterialTask = false) => {
		return (
			<div key={task?.id} className="single-task">
				<div className="d-flex justify-content-between align-items-center">
					<div className="w-100 d-flex align-items-center pl-4">
						{returnStatusIcon(task, deliverableId, style)}
						<div className="regular-12 w-100">
							{isMaterialTask ? <span></span> : null}
							{length === 1 ? task?.stepDescription : task?.stepName}
							{renderInputTargetItem(task)}
							{(task?.formattedTaskStatus === 'COMPLETED' ||
								task?.formattedTaskStatus === 'APPROVED' ||
								task?.formattedTaskStatus === 'LATE_APPROVED') && (
								<>
									<span className="green state ml-2 mr-1">
										<img
											src={`${process.env.PUBLIC_URL}/icons/tick-green.png`}
											className="mr-1"
										/>
										{task?.actualEndDate && moment(task?.actualEndDate).format('D MMM')}
									</span>
									{task?.dateOver > 0 ? (
										<span className="red date-count state mr-1">+{task?.dateOver}d</span>
									) : task?.dateOver < 0 ? (
										<span className="green date-count state mr-1">{task?.dateOver}d</span>
									) : null}
								</>
							)}

							{task?.formattedTaskStatus === 'EXPIRED' && (
								<>
									<span className="status unsafe state ml-2 mr-1">
										<img
											src={`${process.env.PUBLIC_URL}/icons/alert-octagon-white.svg`}
											alt="update"
										/>{' '}
										+{task?.dateOver}d
									</span>
								</>
							)}
						</div>
					</div>

					<div className="date-details">
						<span className="regular-12 mr-2">
							{task?.previousEndDate && (
								<Tooltip
									title={`Due date moved from ${moment(task?.previousEndDate).format('D MMM, YY')} to ${moment(
										task?.endDate
									).format('D MMM, YY')}${
										task?.dueDateUpdatedByName ? ` by ${task.dueDateUpdatedByName}` : ''
									}${task?.revisionCount ? ` · ${task.revisionCount} change(s)` : ''}`}
									placement="top"
									arrow
								>
									<span className="tag mr-2 cursor-pointer">Edited</span>
								</Tooltip>
							)}
						</span>
						<span>
							<DateRangeComponent
								item={{ value1: task?.startDate, value2: task?.endDate }}
								onChange={onChangeDateRange}
								itemId={task?.id}
								single
								disabled={!canEditTaskDueDate(task)}
							/>
						</span>
					</div>
				</div>
			</div>
		);
	};

	const renderDeliveryDetails = () => {
		const selectedData = productList?.find(
			(item) => parseInt(item.rfqId) === parseInt(selectedStyleRfqId)
		);
		return (
			<div className="runnig-order-date-status p-3 bg-white mb-2 d-flex justify-content-between">
				<div className="d-flex align-items-center tna-title">
					<Link
						to={`/suppliers/view/${selectedData?.supplierId}`}
						target={'_blank'}
						className="semibold-16 gray_dark_01 ml-2"
					>
						{selectedData?.supplierName}
					</Link>
				</div>
				<EtdDetails selectedStyleDetails={selectedData} selectedStyle={selectedData} />
			</div>
		);
	};

	const getOrderInfoByDashboardId = async (orderDashboardId) => {
		try {
			const { data } = await getOrderByOrderDashboardId(`?rfqIdList=${orderDashboardId}`);
			if (data?.[0]) {
				let modifiedProjectList = projectList?.map((item) => {
					if (item.id === orderDashboardId) {
						item = data[0];
					}
					return item;
				});
				setProjectList(modifiedProjectList);
			}
		} catch (error) {
			printApiError(error);
		}
	};

	return (
		<div className="tna-container production-tna-container bg-gray">
			<div className="tna-header d-flex align-items-center align-self-center justify-content-between pb-4">
				<div className="d-flex align-items-center align-self-center justify-content-between pb-4">
					<h3 className="regular-16 weight-600 gray_dark_01 m-0">All Styles</h3>
					<EtdDateFilter
						selectedDate={selectedDate}
						handleDateSelection={handleDateSelection}
						uniqueDeliveryDates={uniqueFactoryDeliveryDates}
					/>
				</div>
				<div className="tna-action d-flex">
					<SearchTnaProduct searchValue={searchKey} handleSearch={handleSearch} />
				</div>
			</div>
			<div className="tna-tab-container scroll-x-label p-0 mt-2">
				<div
					className={`${
						productList?.length <= 0
							? 'tna-all-item'
							: 'tna-all-item tna-production-all-item d-flex scroll-x-label'
					}`}
				>
					<div
						className={
							isAllSelected
								? ' all-style-tab d-flex align-items-center justify-content-center cursor-pointer active'
								: 'production-all-style d-flex align-items-center justify-content-center cursor-pointer'
						}
						onClick={() => {
							dispatch(fetchOrderDashboardHeadings(orderId));
							setisAllSelected(true);
							!isAllSelected && getAllStylePlanning(orderId);
							setSelectedStyleRfqId(null);
						}}
					>
						<div className="item-text">
							<p className="gray_dark_01 reguar-12">All Styles</p>
						</div>
					</div>
					{listOfAllProduct.map((style, index) => {
						return (
							<StyleCard
								key={`${style?.id}-${index}`}
								product={style}
								isSelected={parseInt(style?.rfqId) === parseInt(selectedStyleRfqId)}
								onSelectProduct={() => {
									setSelectedStyle(style);
									style?.planningStatus === PLANNING_STATUS.PENDING ||
									style?.planningStatus === PLANNING_STATUS.SUBMITTED
										? onSelectStyleForPending(style?.rfqId)
										: onSelectStyle(style?.rfqId);
								}}
								selectedETD={selectedETD}
								selectedIndex={selectedIndex}
							/>
						);
					})}
				</div>
			</div>

			{isAllSelected && (
				<>
					<div className="tna-list-view-container">
						<div className="tna-list-view-items order-dashboard-tna-view">
							<OrderDashboard
								requestFrom="ORDER_DETAILS"
								orderStore={orderedProjectList}
								callback={fetchDashboardInfo}
								showSummary={false}
								getOrderInfoByDashboardId={getOrderInfoByDashboardId}
								updateOrderStepByOrders={updateOrderStepByOrders}
							/>
						</div>
					</div>
				</>
			)}

			{!isAllSelected && selectedStyle?.planningStatus === PLANNING_STATUS.COMPLETED && (
				<div className="tna-accordion-container">
					{renderDeliveryDetails()}
					{renderSetMembersTab()}
					<div className="accordion mt-1" id="accordionTna">
						<div className="card">
							<div className="card-body">
								<div className="all-tna-tasks">
									{/* Every task is its own row; the deliverable it belongs to is no longer
									    drawn as a heading, so each row carries its own full name. */}
									{selecteStylePlanningDetail?.flatMap((style) => [
										...(style?.stepResponseList || []).map((task) =>
											returnTaskRow(
												task,
												1,
												style?.deliverableResponse?.id,
												style,
												false
											)
										),
										...(style?.materialWiseStepInfoList || [])
											.flatMap((material) => material?.stepResponseList || [])
											.map((task) =>
												returnTaskRow(
													task,
													1,
													style?.deliverableResponse?.id,
													style,
													true
												)
											)
									])}
								</div>
							</div>
						</div>
					</div>
				</div>
			)}

			{((!isAllSelected && selectedStyle?.planningStatus === PLANNING_STATUS.PENDING) ||
				selectedStyle?.planningStatus === PLANNING_STATUS.SUBMITTED) && (
				<CreateTna
					orderId={orderId}
					isShowProducts={false}
					planningStatus={selectedStyle?.planningStatus}
					rfqId={selectedStyleRfqId}
					callback={handleIndividualTna}
					requestFrom="TNA_RUNNING"
					tnaRunningStyleStatus={selectedStyle?.planningStatus}
					getProductList={getProductList}
					selectedStyleDetailsFromRunning={selectedStyle}
					runningProductList={productList}
					setProductList={setProductList}
				/>
			)}
		</div>
	);
};

export default TnaRunning;
